/**
 * Accessible-name helpers.
 *
 * The app leans hard on icon-only buttons — a row of `mdi-pencil` / `mdi-delete-outline` at the end
 * of every table row, a `mdi-close` on every chip. An icon carries no text, so unless something
 * else names the control a screen reader falls back to reading the element type: "button, button,
 * button". These helpers produce the name.
 *
 * Two rules the call sites follow:
 *
 * 1. `aria-label`, not `title`. A `title` attribute *is* a last-resort fallback in the accessible
 *    name computation, but it is not exposed on touch, several screen readers skip it depending on
 *    verbosity settings, and it disappears the moment a tooltip library takes the attribute over.
 *    Where a control already has a `title` for the mouse tooltip, keep it and add `aria-label`
 *    alongside — `aria-label` wins the name, `title` stays the tooltip.
 * 2. Inside a repeating structure the action alone is not a name. Twelve rows of "Delete" tell you
 *    nothing about which record is about to go; the name has to carry the row.
 */

/**
 * Qualifies an action with the record it acts on: `("Delete", "Report 4711")` → `"Delete, Report
 * 4711"`.
 *
 * The separator is a comma rather than a dash or a colon on purpose: screen readers reliably treat
 * it as a prosodic pause, whereas a dash is announced literally ("Delete dash Report 4711") under
 * some verbosity settings and swallowed entirely under others. It is punctuation, not copy, so it
 * is not routed through i18n.
 *
 * Falls back to the bare action when there is nothing to identify the row with — a blank subject
 * would otherwise leave a trailing comma hanging in the announcement.
 */
export function rowActionLabel(action: string, subject?: string | number | null): string {
  const trimmedAction = String(action ?? "").trim()
  const trimmedSubject = subject === null || subject === undefined ? "" : String(subject).trim()

  if (!trimmedSubject) return trimmedAction
  if (!trimmedAction) return trimmedSubject
  return `${trimmedAction}, ${trimmedSubject}`
}

/**
 * Picks the first non-empty value out of the candidates a row might be identified by.
 *
 * Row identifiers are uneven across the entities here: a construction report has a number, a
 * material has a name, a project contact only has a person's name and sometimes not even that.
 * Call sites pass the fields in descending order of usefulness rather than each guarding with its
 * own chain of `||`.
 */
export function rowSubject(...candidates: Array<string | number | null | undefined>): string {
  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined) continue
    const value = String(candidate).trim()
    if (value) return value
  }
  return ""
}

/**
 * Installs `rowActionLabel` as `$rowLabel` so templates can reach it the way they reach `$t`.
 *
 * These labels are written in markup, one per icon button, across dozens of list and panel
 * components that otherwise have no reason to import anything. Threading the function through each
 * one's `setup()` return would be more plumbing than label. `$t` sets the precedent — the typing
 * lives next to it in `vue-shims.d.ts`.
 */
export const a11yPlugin = {
  install(app: { config: { globalProperties: Record<string, unknown> } }) {
    app.config.globalProperties.$rowLabel = rowActionLabel
  },
}
